import dynamic from 'next/dynamic'
import { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import { useConfig } from '../config'
import actionComponents from './action-components'
import { useDetectBot } from '../../hooks/useDetectBot'

const FloatingActions = dynamic(() => import('@bluheadless/ui/src/molecules/floating-actions/floating-actions'), {
	ssr: false,
})

const FloatingActionsContext = createContext()

function reducer(state, action) {
	const { action: actionName, props } = action.payload
	if (!actionName || !Object.keys(state).includes(actionName)) return state

	switch (action.type) {
		case 'activate':
			if (state[actionName].active) return state
			return {
				...state,
				[actionName]: { ...state[actionName], active: true, props: { ...state[actionName].props, ...props } },
			}
		case 'deactivate':
			if (!state[actionName].active) return state
			return { ...state, [actionName]: { ...state[actionName], active: false, props: {} } }
		default:
			throw new Error()
	}
}

const FloatingActionsProvider = ({ children }) => {
	const storeConfig = useConfig()
	const {
		floatingActions: { position, top, singleWidget },
	} = storeConfig

	const { isBot, loading } = useDetectBot()

	const [actions, dispatch] = useReducer(reducer, actionComponents, (initialActionComponents) =>
		Object.keys(initialActionComponents).reduce(
			(acc, actionName) => ({
				...acc,
				[actionName]: {
					active: false,
					props: {},
				},
			}),
			{}
		)
	)

	const activateAction = useCallback((action, actionProps) => {
		dispatch({ type: 'activate', payload: { action, props: actionProps } })
	}, [])

	const deactivateAction = useCallback((action) => {
		dispatch({ type: 'deactivate', payload: { action } })
	}, [])

	const value = useMemo(
		() => [{ actions }, { activateAction, deactivateAction }],
		[actions, activateAction, deactivateAction]
	)

	const show = useMemo(() => Object.entries(actions).some(([, active]) => active), [actions])

	return (
		<FloatingActionsContext.Provider value={value}>
			{children}
			{!loading && !isBot && (
				<FloatingActions show={show} position={position} singleWidget={singleWidget} top={top}>
					{Object.entries(actions)
						// use same actionName as in storeConfig
						.filter(([actionName, { active }]) => storeConfig[actionName]?.enabled && active)
						.map(([action, { props: actionProps }]) => {
							const Component = actionComponents[action]
							if (!Component) return null
							return <Component key={action} {...actionProps} position={position} />
						})}
				</FloatingActions>
			)}
		</FloatingActionsContext.Provider>
	)
}

export default FloatingActionsProvider
export const useFloatingActions = () => useContext(FloatingActionsContext)
